import { type IPublicClientApplication } from '@azure/msal-browser';
import { first } from 'lodash';
import { msalInstance } from './MsalInstance';
import { HttpHeaders, getBearerToken, HttpMethods } from '../constants/api-constants';
import { getConfig } from '../utils/get-config';
import { CONFIG_CODES } from '../types';
import { scopes } from '../constants/msal-config';

class ApiClient {
  private readonly baseUrl: string = getConfig(CONFIG_CODES.REACT_APP_API_HOST);

  private readonly msalInstance: IPublicClientApplication;

  constructor(msalInstance: IPublicClientApplication) {
    this.msalInstance = msalInstance;
  }

  private async acquireToken(): Promise<string> {
    const account = first(msalInstance.getAllAccounts());
    if (!account) {
      throw Error('No account is authenticated.');
    }

    const response = await this.msalInstance.acquireTokenSilent({
      scopes,
      account,
    });
    return response?.accessToken;
  }

  private async request<T>(endpoint: string, options: RequestInit = {}): Promise<T> {
    const token = await this.acquireToken();

    const response = await fetch(`${this.baseUrl}${endpoint}`, {
      ...options,
      headers: {
        ...options.headers,
        Authorization: getBearerToken(token),
      },
    });

    if (!response.ok) {
      const error = await response.text();
      throw new Error(`API Error: ${response.status} - ${error}`);
    }

    return response.json();
  }

  public async get<T>(endpoint: string): Promise<T> {
    return this.request<T>(endpoint, { method: HttpMethods.get, headers: { ...HttpHeaders.acceptJson } });
  }

  public async post<T>(endpoint: string, body: unknown = ''): Promise<T> {
    return this.request<T>(endpoint, {
      method: HttpMethods.post,
      headers: { ...HttpHeaders.contentTypeJson, ...HttpHeaders.acceptJson },
      body: JSON.stringify(body),
    });
  }

  public async put<T>(endpoint: string, body: unknown): Promise<T> {
    return this.request<T>(endpoint, {
      method: HttpMethods.put,
      headers: { ...HttpHeaders.contentTypeJson, ...HttpHeaders.acceptJson },
      body: JSON.stringify(body),
    });
  }

  public async delete<T>(endpoint: string): Promise<T> {
    return this.request<T>(endpoint, { method: HttpMethods.delete, headers: { ...HttpHeaders.acceptJson } });
  }
}

export const apiClient = new ApiClient(msalInstance);
